import {MapLayer} from "@esWebAPI/es/map/layer/MapLayer";
import {PolygonLayer} from "@esWebAPI/es/map/layer/PolygonLayer";
import {PointLayer} from "@esWebAPI/es/map/layer/PointLayer";
import {Map, source} from "azure-maps-control"
import {ILayerView, LayerInfo, LayerUpdateMetadata} from "@esWebAPI/es/model/Layer";
import {DatasourceManager} from "@esWebAPI/es/map/module/DatasourceManager";
import {IEventChannel} from "@esWebAPI/es/model/Models";
import {AzureImageSpriteManager} from "@esWebAPI/es/map/layer/AzureImageSpriteManager";
import {ThematicStylerFactory} from "@esWebAPI/es/map/styler/ThematicStylerFactory";
import {ExpressionFilterSpecification} from "@maplibre/maplibre-gl-style-spec";

export class PointAndPolygonLayer extends MapLayer {

    private readonly spriteManager: AzureImageSpriteManager;
    private readonly stylerFactory: ThematicStylerFactory;

    private _polygonLayer: PolygonLayer|undefined;
    private _pointLayer: PointLayer|undefined;

    private polygonDatasource: source.Source|undefined;
    private pointDatasource: source.Source|undefined;

    constructor(map: Map, layerView: ILayerView, layerInfo: LayerInfo,
                datasourceManager: DatasourceManager,
                spriteManager: AzureImageSpriteManager,
                stylerFactory: ThematicStylerFactory,
                eventChannel: IEventChannel,
                layerFilter?: ExpressionFilterSpecification) {

        super(map, layerView, layerInfo, datasourceManager, eventChannel);

        this.spriteManager = spriteManager;
        this.stylerFactory = stylerFactory;
        this.setLayerFilter(layerFilter)

        this.createLayers().then(() => this._onReady.resolve());
    }

    private async createLayers() {

        const colorThematic = await this.stylerFactory.getColorStyler(this.layerView).getThematic(this.layerView)

        this.polygonDatasource = await this.datasourceManager.create(this.layerView, this.layerInfo, false, this.getDatasourceFilter(colorThematic), `es_${this.layerView.id}-polygon`);
        this.pointDatasource = await this.datasourceManager.create(this.layerView, this.layerInfo, false, this.getDatasourceFilter(colorThematic), `es_${this.layerView.id}-point`);

        this._polygonLayer = new PolygonLayer(this.map, this.layerView, this.layerInfo,
            this.datasourceManager, this.spriteManager, this.stylerFactory, this.eventChannel, this.polygonDatasource);

        this._pointLayer = new PointLayer(this.map, this.layerView, this.layerInfo,
            this.datasourceManager, this.spriteManager, this.stylerFactory, this.eventChannel, this.pointDatasource,
            this._layerFilter);

    }

    public setLayerFilter(filter: ExpressionFilterSpecification | undefined) {
        if (!filter) {
            filter = ["==", ['geometry-type'], "Point"];
        }
        else {
            filter = ["all", ["==", ['geometry-type'], "Point"], filter];
        }
        super.setLayerFilter(filter);
        this._pointLayer?.setLayerFilter(filter);
    }

    public update(_layerUpdateMetadata: LayerUpdateMetadata): Promise<void> {
        return Promise.resolve();
    }



    public hide() {
        this._polygonLayer?.hide();
        this._pointLayer?.hide();
    }

    public show() {
        this._polygonLayer?.show();
        this._pointLayer?.show();
    }

    public destroy() {
        super.destroy();

        // Make sure datasource is not used before calling pointLayer?.destroy()
        this._polygonLayer?.polygonLayer.remove();
        this._polygonLayer?.lineLayer.remove();
        this._polygonLayer?.labelLayer.remove();

        this._pointLayer?.destroy();
        this._polygonLayer?.destroy();
        if (this.polygonDatasource) {
            this.datasourceManager.remove(this.polygonDatasource.getId());
        }
        if (this.pointDatasource) {
            this.datasourceManager.remove(this.pointDatasource.getId());
        }
    }

    public getLayerIds(): string[] {
        const layerIds = [];
        if (this._polygonLayer) {
            layerIds.push(...this._polygonLayer.getLayerIds());
        }
        if (this._pointLayer) {
            layerIds.push(...this._pointLayer.getLayerIds());
        }
        return layerIds;
    }

    protected getColorThematicFilter(): ExpressionFilterSpecification | undefined {
        return undefined;
    }

    protected getSymbolThematicFilter(): ExpressionFilterSpecification | undefined {
        return undefined;
    }

    protected hideFeature(_featureId: number): void {
    }

    protected showFeatures(): void {
    }

    public getActiveAzureLayerId(): string | undefined {
        return this._pointLayer?.getActiveAzureLayerId();
    }

    public get pointLayer(): PointLayer | undefined {
        return this._pointLayer;
    }
}
